<script>
import Form from '@classes/Form'
import BodySitePicker from '@components/body-site-picker.vue'
import { medicalDateFormatter, isBeforeDate } from '@utils/medical-date'
import {
  authComputed,
  authMethods,
  optionsComputed,
  choicesComputed,
} from '@state/helpers'
import { labService } from '@services/LabService'
import { choicesService } from '@services/ChoicesService'
import { diagnosticService } from '@services/DiagnosticService'
import { healthRecordService } from '@services/HealthRecordService'
import store from '@state/store'
import ComboBox from '@components/combo-box.vue'
import hrMixin from '@src/mixins/hr-mixin.vue'
import MedicalDatePickerDialog from '@components/dialogs/medical-date-picker-dialog.vue'
export default {
  page: {
    title: 'Diagnostics Editor',
    meta: [{ name: 'description', content: 'The Diagnostics Editor page.' }],
  },
  components: {
    ComboBox,
    BodySitePicker,
    MedicalDatePickerDialog,
  },
  mixins: [hrMixin],
  props: {
    formChanged: {
      type: Boolean,
      default: false,
    },
    formSaved: {
      type: Boolean,
      default: false,
    },
    formSavedAnother: {
      type: Boolean,
      default: false,
    },
    attachmentDeleted: {
      type: Boolean,
      default: false,
    },
    create: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    id: {
      type: [Number, String],
      default: 0,
    },
    resource: {
      type: String,
      default: 'lab',
    },
    show: {
      type: Boolean,
      default: false,
    },
    unauth: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      row: 'Imaging',
      labForm: new Form({
        category: '',
        conclusion: '',
        is_specific: null,
        date: null,
        date_part: '',
        result: '',
        units: '',
        within_normal_value: '',
        note: '',
        private: false,
      }),
      diagnosticForm: new Form({
        category: '',
        date: null,
        date_part: '',
        note: '',
        private: false,
        body_sites: [],
      }),
      labsAttachments: [],
      originalLabsAttachments: [],
      imagingAttachments: [],
      originalImagingAttachments: [],
      medicalDatePickerDialog: false,
      labError: null,
      diagnosticCategories: {
        labs: {
          value: 'Labs',
          icon: 'mdi-flask-empty-outline',
        },
        imaging: {
          value: 'Imaging',
          icon: 'mdi-clipboard-outline',
        },
      },
      rules: {
        numberRule: (value) => {
          if (value) {
            if (typeof value === 'string') {
              if (!value.trim()) return true
              if (isNaN(Number(value))) {
                return this.$t('health-record.labs.integer-rule-message')
              }
            }
          }
          return true
        },
      },
      showHRActions: true,
    }
  },
  computed: {
    ...authComputed,
    ...optionsComputed,
    ...choicesComputed,
    withinNormalValues() {
      return this.getLabOptions?.within_normal_value
        ? this.getLabOptions.within_normal_value
        : []
    },
    labUnitsFromChoices() {
      return this.getLabUnits ? this.getLabUnits : []
    },
    canSaveLab() {
      if (
        (this.labForm.date || this.labForm.date_part === 'past') &&
        this.row === 'Labs' &&
        this.labForm.category &&
        this.labError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
    canSaveImaging() {
      if (
        (this.diagnosticForm.date ||
          this.diagnosticForm.date_part === 'past') &&
        this.row === 'Imaging' &&
        this.diagnosticForm.category &&
        this.labError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
    computedRecord: {
      get() {
        if (this.row === 'Labs') {
          return this.labForm.private
        } else {
          return this.diagnosticForm.private
        }
      },
      set(value) {
        if (this.row === 'Labs') {
          this.labForm.private = value
        } else {
          this.diagnosticForm.private = value
        }
      },
    },
  },
  watch: {
    labForm: {
      handler(newVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
    diagnosticForm: {
      handler(newVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
    'labForm.is_specific': function(newVal) {
      if (newVal === false) {
        this.labForm.result = ''
        this.labForm.units = ''
      }
      if (newVal === true) {
        this.labForm.within_normal_value = ''
      }
      this.labError = null
    },
    row(newValue, oldValue) {
      if (newValue === 'Labs') {
        this.diagnosticForm.reset()
        this.imagingAttachments = []
      } else if (newValue === 'Imaging') {
        this.labForm.reset()
        this.labsAttachments = []
        this.labForm.is_specific = null
      } else {
        this.diagnosticForm.reset()
        this.imagingAttachments = []
        this.labForm.reset()
        this.labsAttachments = []
        this.labForm.is_specific = null
      }
      this.labError = null
      this.dateError = null
    },
  },
  async mounted() {
    try {
      if (!this.getLabOptions) {
        const labOptions = (await labService.options()).data
        if (labOptions) {
          store.dispatch('options/setLabOptions', labOptions)
        }
      }

      if (!this.getDiagnosticOptions) {
        const diagnosticOptions = (await diagnosticService.options()).data
        if (diagnosticOptions) {
          store.dispatch('options/setDiagnosticOptions', diagnosticOptions)
        }
      }

      if (!this.getLabUnits && !this.unauth) {
        const labUnits = (await choicesService.getUnits()).data
        const unitsToStore = []
        if (labUnits) {
          labUnits.results.forEach((unit) => {
            unitsToStore.push({ key: unit.key, value: unit.label })
          })
          store.dispatch('choices/setLabUnits', unitsToStore)
        }
      }

      if (this.edit) {
        if (this.item.patient_id !== null) {
          this.showHRActions = false
        }
        if (this.item.record_type === 'lab') {
          this.row = 'Labs'
        } else if (this.item.record_type === 'diagnostic') {
          this.row = 'Imaging'
        } else {
          this.row = 'Other'
        }

        this.itemToForm(this.item)
      }
    } catch (error) {
      console.warn(error)
    }
  },
  methods: {
    ...authMethods,
    clearForm() {
      this.labForm.reset()
      this.diagnosticForm.reset()
      this.labsAttachments = []
      this.imagingAttachments = []
      this.message = false
      this.isAnother = false
      this.isContinue = false
      this.labError = null
      this.dateError = null
    },
    setDiagnosticBodySites(selectedBodySites) {
      this.diagnosticForm.body_sites = selectedBodySites
    },
    setMedicalDate(medicalDate, past) {
      const field = this.medicalDatePickerName
      const fieldPart = this.medicalDatePickerName + '_part'

      const medicalDateObject = medicalDateFormatter(medicalDate)

      if (this.row === 'Labs') {
        this.labForm[field] = medicalDateObject.date
        this.labForm[fieldPart] = this.determinePast(past, medicalDateObject)
      } else if (this.row === 'Imaging') {
        this.diagnosticForm[field] = medicalDateObject.date
        this.diagnosticForm[fieldPart] = this.determinePast(
          past,
          medicalDateObject
        )
      }
      this.medicalDatePickerDialog = false
    },
    async getLabResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.labForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (await labService.create(this.labForm.removeEmptyFromData()))
          .data
      }
    },
    async getDiagnosticResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.diagnosticForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await diagnosticService.create(
            this.diagnosticForm.removeEmptyFromData()
          )
        ).data
      }
    },
    async deleteAttachmentLab(id) {
      try {
        const response = await labService.attachmentDelete(id)
        if (response) {
          this.originalLabsAttachments = this.originalLabsAttachments.filter(
            function(obj) {
              return obj.id !== id
            }
          )
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    async deleteAttachmentDiagnostic(id) {
      try {
        const response = await diagnosticService.attachmentDelete(id)
        if (response) {
          this.originalImagingAttachments = this.originalImagingAttachments.filter(
            function(obj) {
              return obj.id !== id
            }
          )
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setErrors(error) {
      if (this.row === 'Labs') {
        this.labForm.onFail(error.response.data)
      } else if (this.row === 'Imaging') {
        this.diagnosticForm.onFail(error.response.data)
      }
      if (this.hasResponse) {
        this.message = true
        this.$emit('update:formSaved', true)
      }
      this.labError = error
    },
    async tryToSave(action) {
      this.resetFlagsAndErrors('labError', action)
      try {
        let labResponse = null
        let diagnosticResponse = null
        if (this.row === 'Labs') {
          if (
            this.labForm.date &&
            isBeforeDate(
              this.labForm.date,
              new Date().toISOString().split('T')[0]
            )
          ) {
            this.dateError = this.$t('start-after-today')
            this.scrollToErrors()
            return
          }
          labResponse = await this.getLabResponse()

          if (labResponse) {
            this.hasResponse = true
            let responseAttachments

            if (this.labsAttachments.length > 0) {
              responseAttachments = (
                await labService.attachments(
                  this.labsAttachments,
                  labResponse.hr_id,
                  'lab'
                )
              ).data
              console.warn(responseAttachments)
            }
          }
        } else if (this.row === 'Imaging') {
          if (
            this.diagnosticForm.date &&
            isBeforeDate(
              this.diagnosticForm.date,
              new Date().toISOString().split('T')[0]
            )
          ) {
            this.dateError = this.$t('start-after-today')
            this.scrollToErrors()
            return
          }
          diagnosticResponse = await this.getDiagnosticResponse()

          if (diagnosticResponse) {
            this.hasResponse = true
            let responseAttachmentsDiagnostic

            if (this.imagingAttachments.length > 0) {
              responseAttachmentsDiagnostic = (
                await diagnosticService.attachments(
                  this.imagingAttachments,
                  diagnosticResponse.hr_id,
                  'diagnostic'
                )
              ).data
              console.warn(responseAttachmentsDiagnostic)
            }
          }
        }
        if (labResponse || diagnosticResponse) {
          await this.fetchWizardData()

          this.message = true
          if (
            action === 'continue' &&
            store.state.calender.addNewRecord === 'true'
          ) {
            this.$emit('update:formSaved', true)
            this.$emit('close-dialog')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }

          if (action === 'continue') {
            this.$emit('update:formSaved', true)
            this.edit ? this.$emit('close-dialog') : this.$emit('back')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'another') {
            this.$emit('update:formSavedAnother', true)
            this.clearForm()
            this.$emit('add-another')
            if (document.getElementById('updateGetAllData'))
              document.getElementById('updateGetAllData').click()
          }
        }
      } catch (error) {
        console.warn(error)
        this.setErrors(error)
        this.isAnother = false
        this.isContinue = false
        this.scrollToErrors()
      } finally {
        this.tryingToSave = false
      }
    },
    itemToForm(item) {
      if (this.row === 'Labs') {
        this.itemToLabForm(item)
      } else {
        this.itemToDiagnosticForm(item)
      }
    },
    itemToLabForm(item) {
      this.labForm.category = item.record.category
      if (item.record.date) {
        this.labForm.date = item.record.date
      }
      this.labForm.is_specific = item.record.is_specific
      this.labForm.date_part = item.record.date_part
      this.labForm.within_normal_value = item.record.within_normal_value?.key
      this.labForm.conclusion = item.record.conclusion
      this.labForm.result = item.record.result
      this.labForm.units = item.record.units
      this.labForm.note = item.note
      this.labForm.private = item.private
      this.originalLabsAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
    itemToDiagnosticForm(item) {
      this.diagnosticForm.category = item.record.category
      if (item.record.date) {
        this.diagnosticForm.date = item.record.date
      }
      if (item.record.body_sites.length > 0) {
        for (let i = 0; i < item.record.body_sites.length; i++) {
          this.diagnosticForm.body_sites.push(item.record.body_sites[i].code)
        }
      }
      this.diagnosticForm.date_part = item.record.date_part
      this.diagnosticForm.note = item.note
      this.diagnosticForm.private = item.private
      this.originalImagingAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
  },
}
</script>

<template>
  <v-container @dragover.prevent @drop="onDrop">
    <v-row
      no-gutters
      :class="$style.hrHeader"
      class="primary--text mt-1 mb-10"
      justify="center"
    >
      <v-row
        no-gutters
        :class="$style.hrHeader"
        class="primary--text mt-1"
        justify="center"
      >
        <div class="mr-4">
          <v-img
            :src="
              require('@assets/images/safehaven/health-record-icons/imaging.svg')
            "
            height="30"
            width="30"
            contain
          ></v-img>
        </div>
        {{ $t('health-record.labs.diagnostics_form_title') }}
      </v-row>
    </v-row>

    <v-fade-transition mode="out-in">
      <div>
        <v-row
          class="offblack--text"
          :class="$style.hrQuestionFull"
          no-gutters
          justify="center"
        >
          What type of imaging is this entry for?
        </v-row>
        <v-row class="mt-10" no-gutters justify="center">
          <div :class="$style.hrComboBoxContainer">
            <ComboBox
              :key="'diagnostic-combo-1'"
              :disabled="show"
              :combo-class="'diagnostic-combo-1'"
              :value.sync="diagnosticForm.category"
              :placeholder="'e.g. X-ray Imaging'"
              :error-messages="
                diagnosticForm.errors.has('category')
                  ? 'Category: ' + diagnosticForm.errors.get('category')
                  : ''
              "
              :fetch-url="unauth ? null : '/api/v1/hr/choices/diagnostic/'"
              rounded
              filled
              background-color="input"
              :class="$style.hrComboBox"
              @keydown="
                clearFormFromErrors(
                  'category',
                  'diagnosticForm',
                  // eslint-disable-next-line vue/comma-dangle
                  'labError'
                )
              "
            ></ComboBox>
          </div>
        </v-row>

        <v-row
          :class="$style.hrQuestionFull"
          class="mt-10 offblack--text"
          no-gutters
          justify="center"
        >
          When was the imaging performed?
        </v-row>
        <v-row v-if="dateError" class="ml-1">
          <v-col cols="12" md="12">
            <p class="error--text">{{ dateError }}</p>
          </v-col>
        </v-row>
        <v-row class="mt-10" no-gutters justify="center">
          <div
            :class="
              mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
            "
          >
            <MedicalDatePickerDialog
              :key="'diagnosticDate-1'"
              :disabled="show"
              :dialog.sync="medicalDatePickerDialog"
              :title="'health-record.labs.imaging-date'"
              :field="diagnosticForm.date"
              :part="diagnosticForm.date_part"
              filled
              background-color="input"
              :error-messages="
                diagnosticForm.errors.has('date')
                  ? 'Diagnostic Date: ' + diagnosticForm.errors.get('date')
                  : ''
              "
              @input="
                clearErrors(
                  'date',
                  'diagnosticForm',
                  // eslint-disable-next-line vue/comma-dangle
                  'labError'
                )
              "
              @setMedicalDate="setMedicalDate"
              @showMedicalDate="showMedicalDate('date')"
            />
          </div>
        </v-row>

        <v-row
          class="mt-10 offblack--text"
          :class="$style.hrQuestionFull"
          no-gutters
          justify="center"
          >Select body part of your body that imaging was performed on
        </v-row>
        <v-row class="mt-10">
          <v-container class="fill-height">
            <v-row class="justify-center">
              <BodySitePicker
                :key="'diagnostic-body-site'"
                :disabled="show"
                :body-sites="diagnosticForm.body_sites"
                @update="setDiagnosticBodySites"
              />
            </v-row>
          </v-container>
        </v-row>

        <v-row class="mt-10">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >Any additional files or notes you'd like to include?
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <SpeechToText
              :key="'diagnostic-speech-1'"
              :disabled="show"
              :value.sync="diagnosticForm.note"
              auto-grow
              rounded
              :class="$style.hrSpeechToText"
              :placeholder="$t('health-record.labs.note')"
            ></SpeechToText>
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <FileUpload
              :key="'diagnostic-file-1'"
              :disabled="show"
              icon
              :height="200"
              :multiple="true"
              :files.sync="imagingAttachments"
              :non-draggable="true"
            ></FileUpload>
          </div>
        </v-row>

        <v-row v-if="edit" class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <AttachmentList
              :key="'attachment-list-2'"
              :disabled="show"
              :attachments="originalImagingAttachments"
              @delete-attachment="deleteAttachmentDiagnostic"
            ></AttachmentList>
          </div>
        </v-row>
      </div>
    </v-fade-transition>

    <div v-if="row === 'Labs' || row === 'Imaging'">
      <ForMyEyesOnly v-if="!show" :value.sync="computedRecord" />
      <div v-if="showHRActions">
        <HrActions
          :can-save="canSaveLab || canSaveImaging"
          :is-another="isAnother"
          :is-continue="isContinue"
          :trying-to-save="tryingToSave"
          :trying-to-delete="tryingToDelete"
          :edit="edit"
          @go-back="goBack"
          @try-to-save="tryToSave"
          @try-to-delete="prepareDeletion"
        />
      </div>
    </div>

    <v-row v-if="labError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{ $t('health-record.labs.error') }}</p>
        <div v-if="labForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in labForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
        <div v-if="diagnosticForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in diagnosticForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="message" class="ml-1">
      <p class="success--text">{{ $t('health-record.labs.message') }}</p>
    </v-row>
    <BaseConfirmDialog
      v-if="confirmDialog"
      :dialog="confirmDialog"
      :message="computedMessage"
      title="Delete Item"
      @cancel="cancel"
      @confirm="deleteItem"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.hrHeader {
  // styleName: Desktop/Display X-Small Bold;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestion {
  // styleName: Desktop/Display X-Small;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionTextLeft {
  // styleName: Desktop/Display X-Small;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrQuestionFull {
  // styleName: Desktop/Display X-Small;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionFullItalic {
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: italic;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrRadio {
  // styleName: Mobile / Display Small;
  font-family: $system-default-font-family;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrComboBoxContainer {
  width: 394px;
}
.hrComboBox {
  border-radius: 15px !important;
}
.hrComboBoxCol {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrTextField {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrTextFieldCol {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrAutoComplete {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrAutoCompleteCol {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrSelect {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrInputDiv {
  width: 394px;
  border-radius: 15px !important;
}
.hrInputDivMobile {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrInputDivAutocomplete {
  border-radius: 15px !important;
}
.hrInputDivMobileAutocomplete {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrSpeechToText {
  max-width: 394px !important;
  background: $color-theme-light-input;
}
.cardsContainer {
  padding-right: 120px !important;
  padding-left: 120px !important;
}
.hrCard {
  border-radius: 16px !important;
}
.activeCard {
  border: 1px solid $color-theme-light-secondary !important;
}
.hrButton {
  border-radius: 8px !important;
}
.hrButtonOutlined {
  border-color: $color-theme-light-primary !important;
  border-radius: 8px !important;
}
.hrRouterLinkText {
  font-family: $system-default-font-family;
  font-size: 20px;
}
.medicalDateDiv {
  width: 394px;
  margin-right: auto;
  margin-left: auto;
}
.medicalDateDivMobile {
  width: 60%;
  margin-right: auto;
  margin-left: auto;
}
</style>
